import { useState } from 'react';

import { Eye, Loader2, Play } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api/axios';

import { TemplateCodeEditor } from '../TemplateCodeEditor';
import { VariableAutocomplete } from '../VariableAutocomplete';

import type { CollectNodeData, FunctionNodeData } from '@repo/shared-types';

interface FunctionConfigProps {
  data: FunctionNodeData;
  onChange: (data: FunctionNodeData) => void;
  /** Collect nodes on this canvas — the live preview runs them as the input. */
  collectNodeDatas?: CollectNodeData[];
}

interface FunctionPreviewResult {
  success: boolean;
  result?: unknown;
  resultCount?: number;
  truncated?: boolean;
  error?: string;
  durationMs: number;
  inputCounts: Record<string, number>;
}

export const FunctionConfig = ({ data, onChange, collectNodeDatas }: FunctionConfigProps) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [testInput, setTestInput] = useState('{}');
  const [previewDate, setPreviewDate] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState<FunctionPreviewResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const handleLivePreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    setPreviewResult(null);
    try {
      const { data: result } = await apiClient.post('/workflows/function-preview', {
        function: data,
        collects: collectNodeDatas ?? [],
        ...(previewDate ? { date: previewDate } : {}),
      });
      setPreviewResult(result as FunctionPreviewResult);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        'Preview failed';
      setPreviewError(Array.isArray(message) ? message.join('; ') : String(message));
    } finally {
      setPreviewing(false);
    }
  };
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    result?: unknown;
    error?: string;
    durationMs: number;
  } | null>(null);

  const update = (fields: Partial<FunctionNodeData>) => {
    onChange({ ...data, ...fields });
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      let parsedInput: unknown = {};
      try {
        parsedInput = JSON.parse(testInput);
      } catch {
        setTestResult({ success: false, error: 'Invalid JSON in test input', durationMs: 0 });
        return;
      }
      const { data: result } = await apiClient.post('/workflows/test-function', {
        code: data.code,
        input: parsedInput,
        timeoutMs: data.timeoutMs ?? 5000,
      });
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        error: err instanceof Error ? err.message : 'Request failed',
        durationMs: 0,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Label</label>
        <Input
          value={data.label || ''}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Function"
        />
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-gray-400">Code (JavaScript)</label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditorOpen(true)}
            className="h-6 text-[10px]"
          >
            Open Editor
          </Button>
        </div>
        {data.code ? (
          <div
            className="bg-muted/30 max-h-28 cursor-pointer overflow-hidden rounded-md border p-2"
            onClick={() => setEditorOpen(true)}
          >
            <pre className="line-clamp-6 font-mono text-[10px] leading-relaxed text-gray-500">
              {data.code}
            </pre>
          </div>
        ) : (
          <p className="text-[10px] text-gray-500">No code yet — click Open Editor to write.</p>
        )}
      </div>

      <TemplateCodeEditor
        value={data.code || ''}
        onChange={(v) => update({ code: v })}
        type="javascript"
        open={editorOpen}
        onOpenChange={setEditorOpen}
      />

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">
          Input (template, e.g. {'{{nodes.stock_1.data}}'})
        </label>
        <VariableAutocomplete
          value={typeof data.input === 'string' ? data.input : JSON.stringify(data.input ?? '')}
          onChange={(v) => update({ input: v })}
          placeholder="{{nodes.stock_1.data}}"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Output Key</label>
        <Input
          value={data.outputKey || 'function_result'}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="function_result"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Results available as {`{{${data.outputKey || 'function_result'}.<field>}}`} downstream.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400">Timeout (ms)</label>
        <Input
          type="number"
          min={100}
          max={10000}
          step={100}
          value={data.timeoutMs ?? 5000}
          onChange={(e) => update({ timeoutMs: parseInt(e.target.value, 10) || 5000 })}
        />
      </div>

      <div className="border-t pt-4">
        <div className="mb-1 flex items-center justify-between">
          <label className="text-xs font-medium text-gray-400">Preview with live data</label>
          <input
            type="date"
            value={previewDate}
            onChange={(e) => setPreviewDate(e.target.value)}
            className="border-input bg-background rounded-md border px-2 py-1 text-xs"
            aria-label="Preview as-if date"
          />
        </div>
        <Button
          onClick={handleLivePreview}
          disabled={previewing || !data.code}
          variant="outline"
          className="w-full"
          size="sm"
        >
          {previewing ? (
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
          ) : (
            <Eye className="mr-2 h-3 w-3" />
          )}
          Preview with live data
        </Button>
        <p className="mt-1 text-[10px] text-gray-500">
          Runs this workflow&apos;s Collect nodes for real (nothing is created), resolves the input
          template against their rows, and executes the code. The optional date previews the run as
          if today were that date.
        </p>

        {previewError && <p className="mt-2 text-[11px] text-red-500">{previewError}</p>}

        {previewResult && (
          <div
            className={`mt-2 rounded-md border p-3 ${previewResult.success ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30'}`}
          >
            <div className="mb-1 flex items-center justify-between">
              <span
                className={`text-xs font-semibold ${previewResult.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}
              >
                {previewResult.success
                  ? previewResult.resultCount !== undefined
                    ? `${previewResult.resultCount} item(s)${previewResult.truncated ? ` — showing first ${Array.isArray(previewResult.result) ? previewResult.result.length : 0}` : ''}`
                    : 'Result'
                  : 'Failed'}
              </span>
              <span className="text-[10px] text-gray-500">
                {Object.entries(previewResult.inputCounts)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(' · ')}
                {' · '}
                {previewResult.durationMs}ms
              </span>
            </div>
            {previewResult.success && previewResult.result !== undefined && (
              <pre className="max-h-64 overflow-auto font-mono text-[10px] text-green-700 dark:text-green-400">
                {JSON.stringify(previewResult.result, null, 2)}
              </pre>
            )}
            {previewResult.error && (
              <p className="text-[10px] text-red-700 dark:text-red-400">{previewResult.error}</p>
            )}
          </div>
        )}
      </div>

      <div className="border-t pt-4">
        <label className="mb-1 block text-xs font-medium text-gray-400">Test Input (JSON)</label>
        <textarea
          value={testInput}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTestInput(e.target.value)}
          className="border-input bg-background w-full rounded-md border p-2 font-mono text-xs"
          rows={4}
          placeholder='{ "dealerName": "Central Hub" }'
        />
        <Button
          onClick={handleTest}
          disabled={testing || !data.code}
          className="mt-2 w-full"
          size="sm"
        >
          {testing ? (
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
          ) : (
            <Play className="mr-2 h-3 w-3" />
          )}
          Test Function
        </Button>
      </div>

      {testResult && (
        <div
          className={`rounded-md border p-3 ${testResult.success ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30'}`}
        >
          <div className="mb-1 flex items-center justify-between">
            <span
              className={`text-xs font-semibold ${testResult.success ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}
            >
              {testResult.success ? 'Passed' : 'Failed'}
            </span>
            <span className="text-[10px] text-gray-500">{testResult.durationMs}ms</span>
          </div>
          {testResult.success && testResult.result !== undefined && (
            <pre className="max-h-48 overflow-auto font-mono text-[10px] text-green-700 dark:text-green-400">
              {JSON.stringify(testResult.result, null, 2)}
            </pre>
          )}
          {testResult.error && (
            <p className="text-[10px] text-red-700 dark:text-red-400">{testResult.error}</p>
          )}
        </div>
      )}
    </div>
  );
};
